import { WidenPrimitives } from '@growthbook/growthbook'
import { setCookie } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { v4 } from 'uuid'
import { SEGMENT_ANONYMOUS_ID_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { AppFeatures } from '@/experimentation/app-features'
import { getExperimentValueFromQuery, getGrowthbook } from '@/experimentation/utils'
import { extractAnonymousIdFromRequest } from '@/services/requests'
import { track } from '@/utils/SegmentNode'
import { getCookieOptions } from '@/utils/cookies/auth-cookie-utils'
import { logger } from '@/utils/logging'
import { getUserUuidFromJwt } from '@/utils/users/usersServer'
import { ServerExperiments } from './types'

export const initializeGrowthbookOnServer = async (
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
) => {
  const anonymousIdFromRequest = extractAnonymousIdFromRequest(req)
  const fallbackAnonymousId = v4()
  const anonymousId = anonymousIdFromRequest ?? fallbackAnonymousId

  // reset cookie if it doesn't exist. This should never happen.
  if (!anonymousIdFromRequest) {
    logger().error('No anonymous ID found in request - growthbook will use fallback', { req })

    setCookie(
      SEGMENT_ANONYMOUS_ID_COOKIE,
      anonymousId,
      getCookieOptions({ req, res, maxAge: Durations.TEN_YEARS_IN_SECONDS }),
    )
  }

  const growthbook = getGrowthbook(
    {
      subscribeToChanges: false,
      backgroundSync: false,
    },
    async (eventName, payload) => {
      return await track(eventName, { anonymousId, ...payload })
    },
  )

  // Give GB two attempts to initialize
  const gbRes = await growthbook.init()
  if (gbRes.error) await growthbook.init()

  const userId = getUserUuidFromJwt(req, res) ?? null

  await growthbook.setAttributes({ id: anonymousId, userId })
  return growthbook
}

export const setupServerExperiments = async (
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
) => {
  const growthbook = await initializeGrowthbookOnServer(req, res)
  return {
    getFeatureValue: growthbook.getFeatureValue.bind(growthbook),
    runInlineExperiment: growthbook.run.bind(growthbook),
    destroy: growthbook.destroy.bind(growthbook),
    anonymousId: growthbook.getAttributes().id,
  }
}

/**
 * @deprecated Use the `runExperimentsOnServer` function instead. It has better performance.
 */
export const runExperimentOnServer = async <T extends keyof AppFeatures>(
  experimentKey: T,
  defaultValue: AppFeatures[T],
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
): Promise<WidenPrimitives<AppFeatures[T]>> => {
  const fromQuery = getSearchParam(req, experimentKey)
  if (fromQuery !== null) {
    return getExperimentValueFromQuery(fromQuery) as WidenPrimitives<AppFeatures[T]>
  }

  const { getFeatureValue, destroy } = await setupServerExperiments(req, res)
  const variation = getFeatureValue(experimentKey, defaultValue)

  destroy()
  return variation
}

interface ExperimentParams<T extends keyof AppFeatures> {
  experimentKey: T
  defaultValue: AppFeatures[T]
  skip?: boolean
}

type ExperimentParamsUnion = {
  [K in keyof AppFeatures]: ExperimentParams<K>
}[keyof AppFeatures]

export type ExperimentParamsArray = ExperimentParamsUnion[]

export async function runExperimentsOnServer(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  experiments: ExperimentParamsArray,
): Promise<ServerExperiments> {
  const { getFeatureValue, destroy } = await setupServerExperiments(req, res)

  const result = experiments.reduce((prev, { experimentKey, defaultValue, skip }) => {
    if (skip) return prev
    const fromQuery = getSearchParam(req, experimentKey)
    const v = fromQuery !== null ? getExperimentValueFromQuery(fromQuery) : getFeatureValue(experimentKey, defaultValue)
    prev[experimentKey] = v
    return prev
  }, {} as ServerExperiments)

  destroy()
  return result
}

export async function runInlineExperimentOnServer(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  experimentKey: string,
  variations: [string, string, ...string[]],
): Promise<string | number | boolean> {
  const { runInlineExperiment, destroy } = await setupServerExperiments(req, res)

  const fromQuery = getSearchParam(req, experimentKey)
  const v =
    fromQuery !== null
      ? getExperimentValueFromQuery(fromQuery)
      : runInlineExperiment({
          key: experimentKey,
          variations,
        }).value

  destroy()
  return v
}

type Variation = { sys: { id: string }; __typename?: string; rolloutVariationId?: string | null } | undefined | null

type VariationReturn<T> = NonNullable<
  Exclude<T, { __typename: 'GrowthbookExperimentMappings' } | { __typename?: 'GrowthbookExperimentMappings' }>
>

const isValidVariation = <T extends Variation>(v: T): v is VariationReturn<T> => {
  return (
    !!v &&
    'sys' in v &&
    typeof v.sys === 'object' &&
    !!v.sys &&
    'id' in v.sys &&
    !!v?.sys?.id &&
    v.__typename !== 'GrowthbookExperimentMappings'
  )
}

const getValidVariations = <T extends Variation>(variations: T[]): VariationReturn<T>[] => {
  return variations.filter(isValidVariation)
}

export const isValidVariationArray = (arr: string[]): arr is [string, string, ...string[]] => {
  return arr.length >= 2
}

export async function runContentfulExperimentOnServer<T extends Variation>(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  rawVariations: T[],
): Promise<VariationReturn<T> | null> {
  try {
    const experimentMapping = rawVariations.find((v) => v?.__typename === 'GrowthbookExperimentMappings')
    const variations = getValidVariations(rawVariations)

    const firstVariation = variations[0]
    const rolledOutVariation = variations.find((v) => v.sys.id === experimentMapping?.rolloutVariationId)

    if (variations.length === 1 && firstVariation) return firstVariation
    if (rolledOutVariation) return rolledOutVariation

    if (!experimentMapping?.sys?.id || variations.length === 0 || !firstVariation) return null

    const variationIds = variations.map((v) => v.sys.id)

    if (!isValidVariationArray(variationIds)) return firstVariation

    const experimentValue = await runInlineExperimentOnServer(req, res, experimentMapping.sys.id, variationIds)

    return variations.find((v) => v.sys.id === experimentValue) || firstVariation
  } catch (err) {
    logger().error(
      'Error running contentful experiments on server',
      {
        experimentMapping: rawVariations.find((v) => v?.__typename === 'GrowthbookExperimentMappings') ?? null,
      },
      err,
    )
    return null
  }
}

function getSearchParam(req: IncomingMessage, key: string): string | null {
  if (!req.url) return null
  const protocol = req.headers['x-forwarded-proto'] === 'https' ? 'https' : 'http'
  const url = new URL(req.url, `${protocol}://${req.headers.host}`)
  return url.searchParams.get(key)
}
